import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check, Trash2, CheckCheck, Inbox } from "lucide-react";
import { useNotifications } from "@/hooks/use-notifications";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

function timeAgo(iso: string) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

const severityClass: Record<string, string> = {
  info: "bg-sky-500/10 text-sky-500 ring-sky-500/30",
  success: "bg-emerald-500/10 text-emerald-500 ring-emerald-500/30",
  warning: "bg-amber-500/10 text-amber-500 ring-amber-500/30",
  error: "bg-rose-500/10 text-rose-500 ring-rose-500/30",
};

export function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { items, unreadCount, markRead, markAllRead, remove } = useNotifications();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className={cn(
          "relative min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition",
          open && "bg-secondary text-foreground"
        )}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-gradient-brand px-1 text-[10px] font-bold leading-none text-white shadow-glow">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-full z-50 mt-2 w-[22rem] rounded-xl border border-border bg-card shadow-card glass overflow-hidden"
          >
            <div className="border-b border-border px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Notifications</div>
                <div className="text-[11px] text-muted-foreground">
                  {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
                </div>
              </div>
              {items.length > 0 && (
                <button
                  onClick={() => markAllRead()}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-primary hover:bg-secondary transition"
                >
                  <CheckCheck className="h-3 w-3" /> Mark all read
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <div className="mx-auto mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
                  <Inbox className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              <ul className="max-h-[24rem] overflow-y-auto divide-y divide-border">
                {items.map((n) => {
                  const Wrap: any = n.link ? Link : "div";
                  const wrapProps: any = n.link ? { to: n.link, onClick: () => { setOpen(false); if (!n.read_at) markRead(n.id); } } : {};
                  return (
                    <li key={n.id} className={cn("group flex items-start gap-3 px-4 py-3 hover:bg-secondary/50 transition", !n.read_at && "bg-primary/[0.03]")}>
                      <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1", severityClass[n.severity] ?? severityClass.info)}>
                        <Bell className="h-3.5 w-3.5" />
                      </span>
                      <Wrap {...wrapProps} className="min-w-0 flex-1 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{n.title}</span>
                          {!n.read_at && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                        </div>
                        {n.body && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{n.body}</p>}
                        <p className="mt-1 text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</p>
                      </Wrap>
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                        {!n.read_at && (
                          <button onClick={() => markRead(n.id)} aria-label="Mark read" className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground">
                            <Check className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => remove(n.id)} aria-label="Delete" className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
